import React, { useCallback, useMemo, useState } from "react";
import styled, { ThemeProvider, keyframes } from "styled-components";
import { space, layout, variant, position, compose } from "styled-system";
import PropTypes from "prop-types";
import Header, { Text } from "../Typography";
import Icon from "../Icon";
import Popover from "../Popover";
import StatusDot from "../StatusDot";
import { CroppedImageWrapper, HeaderAvatarCroppedImage } from "./CroppedImage";
import { hasCssLength } from "./hasCssLength";
import css from "@styled-system/css";
import { themeGet } from "@styled-system/theme-get";

const avatarStyles = compose(space, layout, position);

export const COLOR_CHANGE = keyframes`
0% {
  background-position: -468px 0;
}
100% {
  background-position: 468px 0;
}
`;

// Helper function to resolve customSize values
const resolveCustomSize = (customSize, props) => {
  if (!customSize) return null;

  if (hasCssLength(customSize)) {
    return customSize;
  }
  return themeGet(`avatarScale.${customSize}`)(props) || customSize;
};

const AvatarWrapper = styled("div")(
  css({
    boxSizing: "border-box",
    display: "flex",
    // Top-align image with title so multi-line subtitle/badges don't push
    // the avatar down the middle of the row.
    alignItems: "flex-start",
    position: "relative",
  }),
  (props) =>
    variant({
      prop: "sizing",
      variants: {
        small: {
          fontSize: themeGet("fontSizes.1")(props),
        },
        large: {
          fontSize: themeGet("fontSizes.2")(props),
        },
        default: {},
      },
    }),
  avatarStyles
);

const TextContent = styled("div")(
  css({
    marginLeft: "s",
    lineHeight: "0.8",
    minWidth: 0,
    flex: "1 1 auto",
    marginTop: "auto",
    marginBottom: "auto",
  }),
  (props) =>
    variant({
      prop: "sizing",
      variants: {
        small: {
          fontSize: themeGet("fontSizes.1")(props),
        },
        large: {
          marginLeft: "r",
          fontSize: themeGet("fontSizes.2")(props),
        },
      },
    })
);

const Image = styled("img")(
  (props) =>
    css({
      width:
        resolveCustomSize(props.customSize, props) ||
        themeGet("avatarScale.avatarDefault")(props),
      height:
        resolveCustomSize(props.customSize, props) ||
        themeGet("avatarScale.avatarDefault")(props),
      flex: "0 0 auto",
      backgroundColor: themeGet("colors.surface")(props),
      objectFit: "cover",
      objectPosition: "center",
      objectRepeat: "no-repeat",
      border: "solid 1px " + themeGet("colors.surfaceRaisedBorder")(props),
      display: "block",
      borderRadius: "50%",
    }),
  (props) =>
    variant({
      prop: "sizing",
      variants: {
        small: {
          width: themeGet("avatarScale.avatarSmall")(props),
          height: themeGet("avatarScale.avatarSmall")(props),
        },
        large: {
          width: themeGet("avatarScale.avatarLarge")(props),
          height: themeGet("avatarScale.avatarLarge")(props),
        },
      },
    }),
  (props) =>
    variant({
      prop: "shape",
      variants: {
        square: {
          borderRadius: themeGet("radii.2")(props),
        },
        hexagon: {
          borderRadius: "0",
          clipPath:
            "polygon(50% 0, 95% 25%, 95% 75%, 50% 100%, 5% 75%, 5% 25%)",
        },
        tag: {
          borderRadius: "0",
          clipPath: "polygon(6% 0, 95% 0, 95% 65%, 50% 100%, 6% 65%)",
        },
      },
    })
);

const Shape = styled("div")(
  (props) =>
    css({
      width:
        resolveCustomSize(props.customSize, props) ||
        themeGet("avatarScale.avatarDefault")(props),
      height:
        resolveCustomSize(props.customSize, props) ||
        themeGet("avatarScale.avatarDefault")(props),
      fontWeight: themeGet("fontWeights.2")(props),
      // Omit fontSize when unset so initials inherit AvatarWrapper sizing.
      ...(props.$initialsFontSize ? { fontSize: props.$initialsFontSize } : {}),
      flex: "0 0 auto",
      background: props.gradient
        ? props.gradient
        : `linear-gradient(135deg, ${themeGet("colors.primaryLightest")(props)}, ${themeGet(
            "colors.primaryLight"
          )(props)})`,
      color: themeGet("colors.white")(props),
      border: "0",
      display: "flex",
      borderRadius: "50%",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      textTransform: props.uppercase ? "uppercase" : "none",
      a: {
        color: themeGet("colors.bodyText")(props),
      },
    }),
  (props) =>
    variant({
      prop: "sizing",
      variants: {
        small: {
          width: themeGet("avatarScale.avatarSmall")(props),
          height: themeGet("avatarScale.avatarSmall")(props),
        },
        large: {
          width: themeGet("avatarScale.avatarLarge")(props),
          height: themeGet("avatarScale.avatarLarge")(props),
        },
        default: {},
      },
    }),
  (props) =>
    variant({
      prop: "shape",
      variants: {
        square: {
          borderRadius: themeGet("radii.2")(props),
        },
        hexagon: {
          borderRadius: "0",
          clipPath:
            "polygon(50% 0, 95% 25%, 95% 75%, 50% 100%, 5% 75%, 5% 25%)",
        },
        tag: {
          borderRadius: "0",
          paddingBottom: "4px",
          clipPath: "polygon(6% 0, 95% 0, 95% 65%, 50% 100%, 6% 65%)",
        },
      },
    })
);

const Title = styled(({ titleLevel, ...props }) => {
  const Heading = Header[titleLevel];
  return React.cloneElement(<Heading />, props);
})(
  (props) =>
    css({
      fontSize: themeGet("fontSizes.2")(props),
      "> a": {
        fontSize: themeGet("fontSizes.2")(props),
        color: themeGet("colors.linkText")(props),
      },
      "> div > a": {
        fontSize: themeGet("fontSizes.2")(props),
        color: themeGet("colors.linkText")(props),
      },
      "> div > div > a": {
        fontSize: themeGet("fontSizes.2")(props),
        color: themeGet("colors.linkText")(props),
      },
    }),
  (props) =>
    variant({
      prop: "sizing",
      variants: {
        small: {
          fontSize: themeGet("fontSizes.2")(props),
          "> a": {
            fontSize: themeGet("fontSizes.2")(props),
          },
          "> div > a": {
            fontSize: themeGet("fontSizes.2")(props),
          },
          "> div > div > a": {
            fontSize: themeGet("fontSizes.2")(props),
          },
        },
        large: {
          fontSize: themeGet("fontSizes.3")(props),
          "> a": {
            fontSize: themeGet("fontSizes.3")(props),
          },
          "> div > a": {
            fontSize: themeGet("fontSizes.3")(props),
          },
          "> div > div > a": {
            fontSize: themeGet("fontSizes.3")(props),
          },
        },
      },
    })
);

const Subtitle = styled(Text)(
  (props) =>
    css({
      color: themeGet("colors.subText")(props),
      fontSize: themeGet("fontSizes.1")(props),
    }),
  (props) =>
    variant({
      prop: "sizing",
      variants: {
        small: {
          fontSize: themeGet("fontSizes.0")(props),
          fontWeight: themeGet("fontWeights.1")(props),
        },
        large: {
          fontSize: themeGet("fontSizes.2")(props),
          fontWeight: themeGet("fontWeights.1")(props),
        },
      },
    })
);

const LocalTime = styled(Popover)`
  position: absolute;
  top: 0;
  left: 0;
`;

// HeaderAvatar: frame = white background + padding (the "border") + shadow; fixed outer size 150px (large) / 65px (small)
const HEADER_AVATAR_OUTER = { large: "150px", small: "65px" };
const HEADER_AVATAR_PADDING = { large: 6, small: 4 };

const headerAvatarFrameStyles = ({ $shape, $sizing, theme }) => {
  const padding =
    $sizing === "large"
      ? HEADER_AVATAR_PADDING.large
      : HEADER_AVATAR_PADDING.small;
  return {
    boxSizing: "border-box",
    width:
      $sizing === "large"
        ? HEADER_AVATAR_OUTER.large
        : HEADER_AVATAR_OUTER.small,
    height:
      $sizing === "large"
        ? HEADER_AVATAR_OUTER.large
        : HEADER_AVATAR_OUTER.small,
    padding: `${padding}px`,
    // Frame reads as the surrounding surface so the "border" around the image
    // is white in light mode and dark in dark mode (never a white halo on dark).
    background: theme?.colors?.surface || "#fff",
    boxShadow: `0px 3px 6px ${theme?.colors?.shadowColor || "rgba(0,0,0,0.15)"}`,
    borderRadius:
      $shape === "circle" || !$shape
        ? "50%"
        : $shape === "square"
          ? "6px"
          : "0",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "width 200ms ease-in-out, height 200ms ease-in-out",
  };
};

const HeaderAvatarFrame = styled("div")(headerAvatarFrameStyles, avatarStyles);

const HeaderAvatarInner = styled("div")(({ $size, $shape }) => ({
  width: $size,
  height: $size,
  overflow: "hidden",
  borderRadius: $shape === "circle" || !$shape ? "50%" : "0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "width 200ms ease-in-out, height 200ms ease-in-out",
}));

const HeaderAvatarLoading = styled("div")`
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
  background: linear-gradient(
    to right,
    rgba(0, 0, 0, 0.1) 5%,
    rgba(0, 0, 0, 0.05) 65%,
    rgba(0, 0, 0, 0.1) 70%
  );
  background-size: 200px;
  animation: ${COLOR_CHANGE} 20s linear infinite;
`;

const HeaderAvatarImage = styled("img")(({ $size, $shape }) => ({
  width: $size,
  height: $size,
  transition: "width 200ms ease-in-out, height 200ms ease-in-out",
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
  borderRadius: $shape === "circle" || !$shape ? "50%" : "0",
  display: "block",
}));

const HeaderAvatarShape = styled("div")(({
  $size,
  $shape,
  $gradient,
  $initialsFontSize,
  theme,
}) => {
  const backgroundStyles = $gradient
    ? {
        backgroundImage: $gradient,
        backgroundSize: "cover",
        backgroundPosition: "center center",
      }
    : { background: theme?.colors?.surfaceRaised || "#e6e6e6" };

  return {
    width: $size,
    height: $size,
    transition: "width 200ms ease-in-out, height 200ms ease-in-out",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...backgroundStyles,
    color: "#fff",
    fontWeight: 600,
    fontSize: $initialsFontSize || `calc(${$size} / 2.5)`,
    borderRadius: $shape === "circle" || !$shape ? "50%" : "0",
    ...($shape === "hexagon" && {
      clipPath: "polygon(50% 0, 95% 25%, 95% 75%, 50% 100%, 5% 75%, 5% 25%)",
    }),
    ...($shape === "tag" && {
      clipPath: "polygon(6% 0, 95% 0, 95% 65%, 50% 100%, 6% 65%)",
    }),
  };
});

const HeaderAvatarAction = styled("button")(
  ({ $shape, $sizing, theme }) => ({
    ...headerAvatarFrameStyles({ $shape, $sizing, theme }),
    cursor: "pointer",
    border: "none",
    outline: "none",
    appearance: "none",
    position: "relative",
    zIndex: "5",
    ...($shape === "hexagon" && {
      clipPath: "polygon(50% 0, 95% 25%, 95% 75%, 50% 100%, 5% 75%, 5% 25%)",
    }),
    ...($shape === "tag" && {
      clipPath: "polygon(6% 0, 95% 0, 95% 65%, 50% 100%, 6% 65%)",
    }),
    "&:hover, &:focus": {
      "& > .icon-container": {
        opacity: "1",
      },
    },
  }),
  avatarStyles
);

const IconContainer = styled("div")(({ $shape, $sizing, $size }) => {
  const padding = $sizing === "large" ? 6 : 4;
  return {
    opacity: "0",
    transition: "opacity 0.2s ease-in-out",
    position: "absolute",
    top: `${padding}px`,
    left: `${padding}px`,
    width: $size,
    height: $size,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0, 0, 0, 0.5)",
    zIndex: "2",
    borderRadius: $shape === "circle" || !$shape ? "50%" : "0",
    ...($shape === "hexagon" && {
      clipPath: "polygon(50% 0, 95% 25%, 95% 75%, 50% 100%, 5% 75%, 5% 25%)",
    }),
    ...($shape === "tag" && {
      clipPath: "polygon(6% 0, 95% 0, 95% 65%, 50% 100%, 6% 65%)",
    }),
  };
});

const Avatar = ({
  sizing,
  image,
  croppedAreaPercent,
  gradient,
  fallbackGradient,
  imageAlt,
  initials,
  title,
  titleLevel = "H3",
  subtitle,
  subtitleContent,
  theme,
  shape = "circle",
  localTime,
  uppercase = true,
  customSize,
  initialsFontSize,
  headerAvatar = false,
  onClick,
  loading = false,
  ...props
}) => {
  const hasTitle = !!title;
  const hasSubtitle = !!subtitle;
  const hasSubtitleContent = !!subtitleContent;
  // Falls back to the initials/icon placeholder when the image URL fails to
  // load (e.g. an expired S3 presigned URL) instead of a broken-image icon.
  // Tracks the failed src itself, not just a boolean, so a new image prop
  // (new src) gets a fresh chance to load.
  const [erroredImage, setErroredImage] = useState(null);
  const imageFailed = Boolean(image) && image === erroredImage;
  const handleImageError = useCallback(() => {
    setErroredImage(image);
  }, [image]);
  const alt = useMemo(() => {
    if (imageAlt) {
      return imageAlt;
    } else if (typeof title === "string") {
      return `Avatar for ${title}`;
    }
    // Decorative when no accessible name is available — the surrounding
    // link/context provides the name. Always render alt attribute so the
    // <img> passes the image-alt WCAG check.
    return "";
  }, [imageAlt, title]);

  // Resolve a default icon color suitable on accent/gradient backgrounds
  // Prefer theme tokens if available to avoid hard-coded colors
  const defaultIconColor =
    theme?.colors?.iconOnAccent ||
    theme?.colors?.iconDefault ||
    theme?.colors?.white ||
    "white";

  // HeaderAvatar logic
  if (headerAvatar) {
    // Fixed outer size 150px (large) / 65px (small); inner = outer minus padding
    const innerSize = sizing === "large" ? "138px" : "57px";

    const imgContent =
      image && !imageFailed ? (
        croppedAreaPercent ? (
          <HeaderAvatarCroppedImage
            src={image}
            alt={alt}
            croppedAreaPercent={croppedAreaPercent}
            $size={innerSize}
            $shape={shape}
            onError={handleImageError}
          />
        ) : (
          <HeaderAvatarImage
            src={image}
            alt={alt}
            $size={innerSize}
            $shape={shape}
            onError={handleImageError}
          />
        )
      ) : (
        <HeaderAvatarShape
          $size={innerSize}
          $shape={shape}
          $gradient={gradient || fallbackGradient}
          $initialsFontSize={initialsFontSize}
        >
          {initials ? (
            initials
          ) : shape === "hexagon" ? (
            <Icon icon={["fas", "users"]} color={defaultIconColor} />
          ) : shape === "tag" ? (
            <Icon icon={["fas", "tag"]} color={defaultIconColor} />
          ) : shape === "square" ? (
            <Icon icon={["fas", "users"]} color={defaultIconColor} />
          ) : (
            <Icon icon={["fas", "user"]} color={defaultIconColor} />
          )}
        </HeaderAvatarShape>
      );

    const innerContent = (
      <HeaderAvatarInner $size={innerSize} $shape={shape}>
        {loading ? <HeaderAvatarLoading $size={innerSize} /> : imgContent}
      </HeaderAvatarInner>
    );

    if (onClick && !loading) {
      return (
        <Popover
          direction="right"
          width="fit-content"
          $shape={shape}
          text={shape === "circle" ? "Upload profile picture" : "Change image"}
          headerAvatarSizing={sizing}
          tabIndex="-1"
        >
          <HeaderAvatarAction
            {...props}
            onClick={onClick}
            aria-label="Edit avatar"
            $shape={shape}
            $sizing={sizing}
          >
            <IconContainer
              $shape={shape}
              $sizing={sizing}
              $size={innerSize}
              className="icon-container"
            >
              {shape === "circle" ? (
                <Icon
                  icon={["fas", "camera"]}
                  size={sizing === "large" ? "lg" : "1x"}
                  color={defaultIconColor}
                />
              ) : (
                <Icon
                  icon={["fas", "image"]}
                  size={sizing === "large" ? "lg" : "1x"}
                  color={defaultIconColor}
                />
              )}
            </IconContainer>
            {innerContent}
          </HeaderAvatarAction>
        </Popover>
      );
    }

    return (
      <HeaderAvatarFrame {...props} $shape={shape} $sizing={sizing}>
        {innerContent}
      </HeaderAvatarFrame>
    );
  }

  const component = (
    <AvatarWrapper {...props} sizing={sizing}>
      {image && !imageFailed ? (
        croppedAreaPercent ? (
          <CroppedImageWrapper
            src={image}
            alt={alt}
            croppedAreaPercent={croppedAreaPercent}
            sizing={sizing}
            shape={shape}
            customSize={customSize}
            onError={handleImageError}
          />
        ) : (
          <Image
            src={image}
            sizing={sizing}
            shape={shape}
            alt={alt}
            customSize={customSize}
            onError={handleImageError}
          />
        )
      ) : (
        <Shape
          shape={shape}
          sizing={sizing}
          customSize={customSize}
          $initialsFontSize={initialsFontSize}
          uppercase={uppercase}
          gradient={gradient || fallbackGradient}
        >
          {initials ? (
            initials
          ) : shape === "hexagon" ? (
            <Icon icon={["fas", "users"]} color={defaultIconColor} />
          ) : shape === "tag" ? (
            <Icon icon={["fas", "tag"]} color={defaultIconColor} />
          ) : shape === "square" ? (
            <Icon icon={["fas", "users"]} color={defaultIconColor} />
          ) : (
            <Icon icon={["fas", "user"]} color={defaultIconColor} />
          )}
        </Shape>
      )}
      {(hasTitle || hasSubtitle) && (
        <TextContent sizing={sizing} theme={theme}>
          {hasTitle && (
            <Title titleLevel={titleLevel} sizing={sizing} theme={theme}>
              {title}
            </Title>
          )}
          {hasSubtitle && <Subtitle sizing={sizing}>{subtitle}</Subtitle>}
          {hasSubtitleContent && subtitleContent}
        </TextContent>
      )}
      {localTime && (
        <LocalTime
          direction="topRight"
          textAlign="center"
          width="fit-content"
          text={localTime}
        >
          <StatusDot icon variant="secondary">
            <Icon icon={["fas", "clock"]} color="white" transform="grow-2" />
          </StatusDot>
        </LocalTime>
      )}
    </AvatarWrapper>
  );

  return theme ? (
    <ThemeProvider theme={theme}>{component}</ThemeProvider>
  ) : (
    component
  );
};

Avatar.propTypes = {
  /** Changes the sizing of the Avatar component */
  sizing: PropTypes.oneOf(["small", "large", "default"]),
  /* If specifying custom sizing, provide a string with the size including units (e.g. "65px") */
  customSize: PropTypes.string,
  /** Overrides initials font-size on the avatar shape (e.g. "0.8rem"). Unset inherits from the wrapper. */
  initialsFontSize: PropTypes.string,
  /** Changes the shape of the Avatar component */
  shape: PropTypes.oneOf(["circle", "square", "hexagon", "tag"]), // circle is the default so doesn't explicitly need to be specified
  /** Specifies a source path for an image */
  image: PropTypes.string,
  /** Specifies crop data for the image with x, y, width, and height properties */
  croppedAreaPercent: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }),
  /** Specifies the gradient for the avatar background */
  gradient: PropTypes.string,
  /** Specifies the fallback gradient when no image is provided */
  fallbackGradient: PropTypes.string,
  /** Specifies the alt text for an image. This must be specified if image prop is used and title prop is not a string. */
  imageAlt: (props, propName) => {
    if (
      props.image &&
      props.title &&
      typeof props.title !== "string" &&
      (props[propName] == null || props[propName] === "")
    ) {
      return new Error(
        `Missing prop \`${propName}\` not specified for Avatar component. When \`image\` is provided, \`${propName}\` is required.`
      );
    }
    if (props[propName] && typeof props[propName] !== "string") {
      return new Error(
        `Invalid propType \`${propName}\` supplied to Avatar component. Expected \`string\`, received \`${typeof props[
          propName
        ]}\`.`
      );
    }
    return null;
  },
  /** Specifies initials of person if available */
  initials: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /** Specifies custom content for avatar subtitle */
  subtitleContent: PropTypes.oneOfType([PropTypes.element, PropTypes.node]),
  /** Specifies whether to use uppercase for initials */
  uppercase: PropTypes.bool,
  /** Specifies title / name as just plain text, or an element like a hyperlink or react router link */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /** Specifies the component to wrap the heading, defaults to H3 */
  titleLevel: PropTypes.oneOf(["H1", "H2", "H3", "H4", "H5", "H6"]),
  /** Specifies subtitle / role */
  subtitle: PropTypes.string,
  /** Specifies local time */
  localTime: PropTypes.string,
  /** Specifies the colour theme */
  theme: PropTypes.object,
  /** Specifies whether to render the avatar in headerAvatar mode */
  headerAvatar: PropTypes.bool,
  /** Specifies the onClick handler for the action button in headerAvatar mode */
  onClick: PropTypes.func,
  /** Specifies whether to render the avatar in loading state */
  loading: PropTypes.bool,
};

export default Avatar;
